import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '../../App';
import axios from 'axios';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { toast } from 'sonner';
import { COUNTRY_CODES } from '../../data/countryCodes';
import {
  Building2, Save, Plus, Trash2, Check, ChevronsUpDown,
  Phone, Mail, MapPin, Globe, User, FileText, Receipt,
  AlertTriangle, Users, Lock
} from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────
const toTitleCase = s => s ? s.charAt(0).toLocaleUpperCase('tr') + s.slice(1).toLocaleLowerCase('tr') : s;
const onlyDigits = (s, max = 10) => (s || '').replace(/\D/g, '').slice(0, max);
const isValidEmail = e => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const EMPTY_CONTACT = {
  id: '', name: '', role: '',
  mobile_country_code: '+90', mobile_number: '',
  email: '', gender: '', birth_date: '',
};

const SECTORS = [
  'Gıda & İçecek', 'Tekstil & Moda', 'İnşaat & Yapı', 'Teknoloji & Yazılım',
  'Makine & Ekipman', 'Mobilya & Dekorasyon', 'Sağlık & Medikal', 'Otomotiv',
  'Tarım & Hayvancılık', 'Enerji', 'Turizm & Otelcilik', 'Eğitim', 'Finans & Sigorta', 'Diğer',
];

const EMPLOYEE_RANGES = ['1–10', '11–50', '51–200', '201–500', '500+'];

// ── PhoneCodeSelect ───────────────────────────────────────────────────────────
function PhoneCodeSelect({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const sel = COUNTRY_CODES.find(c => c.code === value) || { flag: '🌍', code: value };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-28 justify-between font-normal h-10 text-sm shrink-0 px-2" data-testid={testId}>
          <span>{sel.flag} {sel.code}</span>
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Ülke ara..." className="h-9" />
          <CommandList>
            <CommandEmpty>Bulunamadı</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map(c => (
                <CommandItem key={c.code + c.name} value={`${c.name} ${c.code}`}
                  onSelect={() => { onChange(c.code); setOpen(false); }} className="text-sm">
                  <span className="mr-2">{c.flag}</span>{c.name}
                  <span className="text-gray-400 ml-1">{c.code}</span>
                  {value === c.code && <Check className="ml-auto h-3.5 w-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── CountrySelect ─────────────────────────────────────────────────────────────
function CountrySelect({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const sel = COUNTRY_CODES.find(c => c.name === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal h-10 text-sm" data-testid={testId}>
          <span className={sel ? 'text-gray-900' : 'text-gray-400'}>
            {sel ? `${sel.flag} ${sel.name}` : 'Ülke seçin...'}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Ülke ara..." className="h-9" />
          <CommandList>
            <CommandEmpty>Bulunamadı</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map(c => (
                <CommandItem key={c.code + c.name} value={c.name}
                  onSelect={() => { onChange(c.name); setOpen(false); }} className="text-sm">
                  <span className="mr-2">{c.flag}</span>{c.name}
                  {value === c.name && <Check className="ml-auto h-3.5 w-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Section Card ──────────────────────────────────────────────────────────────
function SectionCard({ icon: Icon, iconBg = 'bg-teal-500', title, children }) {
  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader className="pb-3 pt-4 px-5 border-b border-gray-100">
        <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${iconBg}`}>
            <Icon className="w-3.5 h-3.5 text-white" />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 py-4">
        {children}
      </CardContent>
    </Card>
  );
}

// ── Profile completion ────────────────────────────────────────────────────────
function completionOf(f) {
  if (!f) return 0;
  const fields = [
    f.short_name, f.full_name, f.sector, f.email,
    f.fixed_phone_number, f.address, f.city, f.country,
    f.tax_number, f.tax_office,
  ];
  const filled = fields.filter(v => v && String(v).trim()).length;
  return Math.round((filled / fields.length) * 100);
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TenantProfileEditor() {
  const { user } = useContext(AuthContext);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [contactEmailErrors, setContactEmailErrors] = useState({});
  const token = () => localStorage.getItem('token');

  useEffect(() => { fetchProfile(); }, []); // eslint-disable-line

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tenant-profile`, { headers: { Authorization: `Bearer ${token()}` } });
      const profileData = res.data;
      // Contacts boşsa kullanıcının adı ve email'i ile ilk kişiyi otomatik ekle
      if ((!profileData.contacts || profileData.contacts.length === 0) && user?.email) {
        profileData.contacts = [{
          id: crypto.randomUUID(),
          name: profileData.contact_person || user?.name || '',
          role: '',
          mobile_country_code: '+90',
          mobile_number: '',
          email: user.email,
          gender: '',
          birth_date: '',
        }];
      }
      setForm(profileData);
    } catch { toast.error('Profil yüklenemedi'); }
    finally { setLoading(false); }
  };

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    if (fieldErrors[key]) setFieldErrors(e => { const n = {...e}; delete n[key]; return n; });
  };

  const save = async () => {
    // Zorunlu alan validasyonu
    const errsF = {};
    if (!form.short_name?.trim()) errsF.short_name = 'Şirket Kısa Adı zorunludur';
    if (!form.full_name?.trim()) errsF.full_name = 'Şirket Ünvanı zorunludur';
    if (Object.keys(errsF).length) {
      setFieldErrors(errsF);
      toast.error('Zorunlu alanları doldurun');
      return;
    }
    setFieldErrors({});
    if (!isValidEmail(form.email)) { setEmailError('Geçerli bir e-posta adresi girin'); return; }
    setEmailError('');
    const errs = {};
    (form.contacts || []).forEach((c, i) => { if (!isValidEmail(c.email)) errs[i] = 'Geçersiz e-posta'; });
    if (Object.keys(errs).length) { setContactEmailErrors(errs); return; }
    setContactEmailErrors({});
    setSaving(true);
    try {
      await axios.put(`${API}/tenant-profile`, form, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Şirket bilgileri kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const addContact = () => {
    if ((form.contacts || []).length >= 10) return toast.warning('Maksimum 10 yetkili kişi eklenebilir');
    setForm(f => ({ ...f, contacts: [...(f.contacts || []), { ...EMPTY_CONTACT, id: crypto.randomUUID() }] }));
  };

  const updateContact = (idx, key, val) => setForm(f => {
    const c = [...f.contacts]; c[idx] = { ...c[idx], [key]: val }; return { ...f, contacts: c };
  });
  const removeContact = idx => setForm(f => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/logo`, fd, {
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' }
      });
      set('logo_url', res.data.url);
      toast.success('Logo yüklendi');
    } catch { toast.error('Logo yüklenemedi'); }
  };

  if (loading) return <div className="py-10 text-center text-sm text-gray-400">Yükleniyor...</div>;
  if (!form) return null;

  const pct = completionOf(form);
  const displayName = form.short_name || form.full_name || 'Şirket Adı';
  const createdStr = form.created_at
    ? new Date(form.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })
    : null;

  return (
    <div className="space-y-4 max-w-2xl">

      {/* ── Profile Header Card ─────────────────────────────────────────── */}
      <Card className="border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-gray-100 px-5 py-4">
          <div className="flex items-start gap-4">
            {/* Logo / Avatar */}
            <div className="relative shrink-0 group">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="w-16 h-16 rounded-xl object-contain border border-gray-200 bg-white shadow-sm" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-teal-600 flex items-center justify-center shadow-sm">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
              )}
              <label htmlFor="logo-upload" className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                <span className="text-white text-[10px] font-medium text-center leading-tight px-1">Logo Yükle</span>
              </label>
              <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadLogo(e.target.files[0]); }} data-testid="logo-upload-input" />
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-gray-900 truncate">{displayName}</h2>
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium border border-green-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Aktif
                </span>
              </div>
              {form.sector && <p className="text-xs text-gray-500 mt-0.5">{form.sector}</p>}
              {createdStr && <p className="text-xs text-gray-400 mt-0.5">{createdStr} tarihinden beri üye</p>}

              {/* Completion bar */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">Profil Tamamlanma</span>
                  <span className="text-xs font-semibold text-teal-700">{pct}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-teal-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {pct < 100 && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Daha fazla özellik için profilinizi tamamlayın
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Temel Şirket Bilgileri ──────────────────────────────────────── */}
      <SectionCard icon={Building2} iconBg="bg-teal-600" title="Temel Şirket Bilgileri">
        <div className="space-y-4">

          {/* Hesap E-postası (read-only) */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1">
              <Lock className="w-3 h-3 text-gray-400" /> Hesap E-postası
            </Label>
            <div className="relative">
              <Input
                value={user?.email || ''}
                disabled
                className="bg-gray-50 text-gray-400 cursor-not-allowed pr-32"
                data-testid="profile-account-email"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded select-none">
                Değiştirilemez
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Şirket Kısa Adı <span className="text-red-500">*</span></Label>
              <Input value={form.short_name || ''} onChange={e => set('short_name', e.target.value)}
                placeholder="Şirket kısa adını yazınız"
                className={fieldErrors.short_name ? 'border-red-400' : ''}
                data-testid="profile-short-name" />
              {fieldErrors.short_name && <p className="text-xs text-red-500">{fieldErrors.short_name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Şirket Ünvanı <span className="text-red-500">*</span></Label>
              <Input value={form.full_name || ''} onChange={e => set('full_name', e.target.value)}
                placeholder="Şirket ünvanını yazınız"
                className={fieldErrors.full_name ? 'border-red-400' : ''}
                data-testid="profile-full-name" />
              {fieldErrors.full_name && <p className="text-xs text-red-500">{fieldErrors.full_name}</p>}
            </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Sektör</Label>
              <select
                value={form.sector || ''}
                onChange={e => set('sector', e.target.value)}
                className="w-full h-10 border border-gray-200 rounded-md px-3 text-sm bg-white focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                data-testid="profile-sector"
              >
                <option value="">Sektör seçin...</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">İletişim Sorumlusu</Label>
              <Input value={form.contact_person || ''} onChange={e => set('contact_person', e.target.value)}
                placeholder="Ad Soyad" data-testid="profile-contact-person" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">İletişim E-postası</Label>
              <Input type="email" value={form.email || ''}
                onChange={e => { set('email', e.target.value); if (emailError) setEmailError(''); }}
                onBlur={e => !isValidEmail(e.target.value) && e.target.value && setEmailError('Geçerli bir e-posta adresi girin')}
                placeholder="info@firma.com"
                className={emailError ? 'border-red-400' : ''}
                data-testid="profile-email" />
              {emailError && <p className="text-xs text-red-500">{emailError}</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Web Sitesi</Label>
              <Input value={form.website || ''} onChange={e => set('website', e.target.value)}
                placeholder="www.firma.com" data-testid="profile-website" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Çalışan Sayısı</Label>
              <select
                value={form.employee_count || ''}
                onChange={e => set('employee_count', e.target.value)}
                className="w-full h-10 border border-gray-200 rounded-md px-3 text-sm bg-white focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                data-testid="profile-employee-count"
              >
                <option value="">Seçin...</option>
                {EMPLOYEE_RANGES.map(r => <option key={r} value={r}>{r} kişi</option>)}
              </select>
            </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Şirket Açıklaması</Label>
            <Textarea value={form.description || ''} onChange={e => set('description', e.target.value)}
              placeholder="Şirketiniz hakkında kısa bir açıklama..."
              rows={3} className="resize-none text-sm" data-testid="profile-description" />
          </div>

          <div className="flex justify-end pt-1">
            <Button onClick={save} disabled={saving}
              className="bg-green-600 hover:bg-green-700 gap-1.5 px-5" data-testid="save-profile-btn">
              <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ── Adres Bilgileri ─────────────────────────────────────────────── */}
      <SectionCard icon={MapPin} iconBg="bg-blue-500" title="Adres Bilgileri">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Şirket Adresi</Label>
            <Input value={form.address || ''} onChange={e => set('address', e.target.value)}
              placeholder="Sokak / Mahalle / Cadde" data-testid="profile-address" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Ülke</Label>
            <CountrySelect value={form.country || ''} onChange={v => set('country', v)} testId="profile-country" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Şehir</Label>
              <Input value={form.city || ''} onChange={e => set('city', toTitleCase(e.target.value))}
                placeholder="İstanbul" data-testid="profile-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Posta Kodu</Label>
              <Input value={form.zip_code || ''} onChange={e => set('zip_code', e.target.value)}
                placeholder="34000" data-testid="profile-zip" />
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 gap-1.5 px-5">
              <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ── Vergi Bilgileri ─────────────────────────────────────────────── */}
      <SectionCard icon={Receipt} iconBg="bg-purple-500" title="Vergi Bilgileri">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Vergi Numarası</Label>
              <Input value={form.tax_number || ''}
                onChange={e => set('tax_number', e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="1234567890" inputMode="numeric" data-testid="profile-tax-number" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Vergi Dairesi</Label>
              <Input value={form.tax_office || ''} onChange={e => set('tax_office', e.target.value)}
                placeholder="Kadıköy Vergi Dairesi" data-testid="profile-tax-office" />
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 gap-1.5 px-5">
              <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ── İletişim Bilgileri ──────────────────────────────────────────── */}
      <SectionCard icon={Phone} iconBg="bg-orange-500" title="İletişim Bilgileri">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Sabit Telefon</Label>
            <div className="flex gap-2">
              <PhoneCodeSelect value={form.fixed_phone_code || '+90'}
                onChange={v => set('fixed_phone_code', v)} testId="profile-fixed-code" />
              <Input value={form.fixed_phone_number || ''}
                onChange={e => set('fixed_phone_number', onlyDigits(e.target.value))}
                placeholder="2121234567" maxLength={10} inputMode="numeric"
                className="flex-1" data-testid="profile-fixed-number" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Mobil Telefon</Label>
            <div className="flex gap-2">
              <PhoneCodeSelect value={form.mobile_code || '+90'}
                onChange={v => set('mobile_code', v)} testId="profile-mobile-code" />
              <Input value={form.mobile_number || ''}
                onChange={e => set('mobile_number', onlyDigits(e.target.value))}
                placeholder="5001234567" maxLength={10} inputMode="numeric"
                className="flex-1" data-testid="profile-mobile-number" />
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 gap-1.5 px-5">
              <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ── Yetkili Kişiler ─────────────────────────────────────────────── */}
      <SectionCard icon={Users} iconBg="bg-indigo-500" title={`Yetkili Kişiler (${(form.contacts || []).length}/10)`}>
        <div className="space-y-3">
          {(form.contacts || []).map((c, i) => (
            <div key={c.id || i} className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Kişi #{i + 1}
                </span>
                <button onClick={() => removeContact(i)}
                  className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                  data-testid={`remove-contact-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Ad Soyad *</Label>
                  <Input value={c.name} onChange={e => updateContact(i, 'name', e.target.value)}
                    placeholder="Ad Soyad" className="h-8 text-sm" data-testid={`contact-name-${i}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Görevi *</Label>
                  <Input value={c.role} onChange={e => updateContact(i, 'role', e.target.value)}
                    placeholder="Genel Müdür" className="h-8 text-sm" data-testid={`contact-role-${i}`} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Mobil Telefon</Label>
                <div className="flex gap-2">
                  <PhoneCodeSelect value={c.mobile_country_code || '+90'}
                    onChange={v => updateContact(i, 'mobile_country_code', v)}
                    testId={`contact-mobile-code-${i}`} />
                  <Input value={c.mobile_number || ''}
                    onChange={e => updateContact(i, 'mobile_number', onlyDigits(e.target.value))}
                    placeholder="5XX XXX XXXX" maxLength={10} inputMode="numeric"
                    className="h-10 text-sm flex-1" data-testid={`contact-mobile-${i}`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">E-posta</Label>
                  <Input type="email" value={c.email || ''}
                    onChange={e => { updateContact(i, 'email', e.target.value); if (contactEmailErrors[i]) setContactEmailErrors(er => { const n = {...er}; delete n[i]; return n; }); }}
                    onBlur={e => !isValidEmail(e.target.value) && e.target.value && setContactEmailErrors(er => ({...er, [i]: 'Geçersiz e-posta'}))}
                    placeholder="kisi@firma.com" className={`h-8 text-sm ${contactEmailErrors[i] ? 'border-red-400' : ''}`}
                    data-testid={`contact-email-${i}`} />
                  {contactEmailErrors[i] && <p className="text-xs text-red-500">{contactEmailErrors[i]}</p>}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Cinsiyet</Label>
                  <div className="flex gap-1.5 h-8">
                    {[{ value: 'erkek', label: 'Erkek' }, { value: 'kadın', label: 'Kadın' }].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => updateContact(i, 'gender', c.gender === opt.value ? '' : opt.value)}
                        className={`flex-1 text-xs rounded border transition-colors ${c.gender === opt.value ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                        data-testid={`contact-gender-${opt.value}-${i}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Button type="button" variant="outline" size="sm" className="w-full gap-1.5 border-dashed"
            onClick={addContact} disabled={(form.contacts || []).length >= 10}
            data-testid="add-contact-btn">
            <Plus className="w-4 h-4" /> Yetkili Kişi Ekle
          </Button>

          {(form.contacts || []).length > 0 && (
            <div className="flex justify-end pt-1">
              <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 gap-1.5 px-5">
                <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ── Tehlikeli Bölge ─────────────────────────────────────────────── */}
      <Card className="border border-red-200 shadow-sm">
        <CardContent className="px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-red-700">Tehlikeli Bölge</h4>
              <p className="text-xs text-red-500 mt-0.5">Bu bölgedeki işlemler geri alınamaz. Dikkatli olun.</p>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
